/**
 * GracePeriodBanner - Subscription status warning banner
 * Single component, single source of truth.
 * Logic: isRestricted → red, isInGracePeriod → severity-based, pending invoice → blue.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utility/utility';
import { formatCurrency } from '../../utility/currencyFormatter';

export enum GRACE_PERIOD_SEVERITY {
  INFO = 'info',
  WARNING = 'warning',
  CRITICAL = 'critical',
}

export interface GracePeriodBannerProps {
  isRestricted: boolean;
  isInGracePeriod: boolean;
  unpaidInvoiceCount: number;
  gracePeriodEndDate?: string;
  daysRemaining?: number;
  outstandingAmount: number;
  severity?: GRACE_PERIOD_SEVERITY;
  isTrial?: boolean;
  trialDaysRemaining?: number | null;
  showViewPlansButton?: boolean;
}

const GracePeriodBanner: React.FC<GracePeriodBannerProps> = ({
  isRestricted,
  isInGracePeriod,
  unpaidInvoiceCount,
  gracePeriodEndDate,
  daysRemaining,
  outstandingAmount,
  severity = GRACE_PERIOD_SEVERITY.INFO,
  isTrial = false,
  trialDaysRemaining = null,
  showViewPlansButton = true,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Dismissible only for info-level banners (pending invoice or early grace period)
  const [dismissed, setDismissed] = useState(false);

  const handlePayNow = () => {
    navigate('/billing-and-plans?tab=billing');
  };

  // Amounts use the shared formatter (see import): an organization pays in LKR
  // only when it is Sri Lankan, otherwise in USD. The org's currency is stored
  // at login from `profile.orgcurrency` and the formatter falls back to USD, so
  // this banner shows the same currency as the rest of the app instead of a
  // hardcoded LKR.

  if (isTrial) {
    return (
      <div
        className="flex items-center justify-between p-4 rounded-lg mb-2.5 shadow-sm border-b-2 flex-col lg:flex-row gap-4"
        style={{ backgroundColor: '#e8eefa', borderColor: '#314d89' }}
      >
        <div className="flex items-center gap-4 flex-1">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#314d89' }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="2" />
              <path
                d="M12 7v5l3 3"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <div className="flex items-start gap-1 flex-wrap">
              <span className="text-base font-bold leading-6" style={{ color: '#314c84' }}>
                {t('billing.banner.trial_title', 'You are in Trial Period!')}
              </span>
              <span className="text-base font-normal leading-6 flex-1" style={{ color: '#314c84' }}>
                {t('billing.banner.trial_full_access', 'Full access to all features.')}
                {trialDaysRemaining !== null && trialDaysRemaining > 0
                  ? ' ' +
                    t('billing.banner.trial_ends_in', 'Trial ends in {{count}} day(s).', {
                      count: trialDaysRemaining,
                    })
                  : ' ' + t('billing.banner.trial_expires_today', 'Your trial expires today.')}
              </span>
            </div>
            <div className="text-sm font-normal leading-5 opacity-90" style={{ color: '#314c84' }}>
              {t(
                'billing.banner.trial_upgrade_hint',
                'Upgrade to a paid plan to continue using all features without interruption.',
              )}
            </div>
          </div>
        </div>
        {showViewPlansButton && (
          <button
            className="flex items-center justify-center gap-3 py-2.5 px-5 border-none rounded-lg text-white text-base font-normal leading-6 cursor-pointer transition-colors duration-200 h-11 flex-shrink-0 w-full lg:w-auto"
            style={{ backgroundColor: '#6c4e9a' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#5a3d82';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#6c4e9a';
            }}
            onClick={() => navigate('/billing-and-plans')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path
                d="M3 7h18M3 7a2 2 0 00-2 2v8a2 2 0 002 2h18a2 2 0 002-2V9a2 2 0 00-2-2M3 7V5a2 2 0 012-2h14a2 2 0 012 2v2"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <path d="M8 14h.01M12 14h.01" stroke="white" strokeWidth="2" strokeLinecap="round" />
            </svg>
            {t('billing.banner.view_plans', 'View Plans')}
          </button>
        )}
      </div>
    );
  }

  if (isRestricted) {
    return (
      <div
        className="flex items-center justify-between p-4 rounded-lg mb-2.5 shadow-sm border-b-2 flex-col lg:flex-row gap-4"
        style={{ backgroundColor: '#fef2f2', borderColor: '#ef4444' }}
      >
        <div className="flex items-center gap-4 flex-1">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#ef4444' }}
          >
            <svg width="22" height="20" viewBox="0 0 22.016 20.014" fill="none">
              <path
                d="M20.748 16.014L12.748 2.014C12.574 1.706 12.321 1.45 12.015 1.272C11.709 1.094 11.362 1 11.008 1C10.654 1 10.307 1.094 10.001 1.272C9.695 1.45 9.442 1.706 9.268 2.014L1.268 16.014C1.092 16.319 0.999 16.666 1 17.018C1.001 17.371 1.095 17.717 1.272 18.022C1.45 18.326 1.705 18.579 2.012 18.753C2.318 18.927 2.665 19.017 3.018 19.014H19.018C19.369 19.013 19.714 18.921 20.017 18.745C20.321 18.57 20.573 18.317 20.749 18.013C20.924 17.709 21.016 17.364 21.016 17.013C21.016 16.663 20.923 16.318 20.748 16.014Z"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11 8V12"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11 16H11.01"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <div className="flex items-start gap-1 flex-wrap">
              <span className="text-base font-bold leading-6 text-gray-800">
                {t('grace.service_restricted', 'Service Restricted:')}
              </span>
              <span className="text-base font-normal leading-6 flex-1 text-gray-800">
                {t(
                  'grace.restricted_msg',
                  '{{count}} overdue invoice(s) totaling {{amount}}. Your access has been restricted.',
                  { count: unpaidInvoiceCount, amount: formatCurrency(outstandingAmount) },
                )}
              </span>
            </div>
            <div className="text-sm font-normal leading-5 opacity-90 text-gray-700">
              {t(
                'grace.settle_immediately',
                'Please settle all outstanding invoices immediately to restore full access.',
              )}
            </div>
          </div>
        </div>
        <button
          className="flex items-center justify-center gap-3 py-2.5 px-5 border-none rounded-lg text-white text-base font-normal leading-6 cursor-pointer transition-colors duration-200 h-11 flex-shrink-0 w-full lg:w-auto"
          style={{ backgroundColor: '#ef4444' }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#dc2626';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#ef4444';
          }}
          onClick={handlePayNow}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2.5 4.5H13.5V13.5H2.5V4.5Z" stroke="white" strokeWidth="1.5" fill="none" />
            <path d="M2.5 7.5H13.5" stroke="white" strokeWidth="1.5" />
            <path d="M5 10.5H7.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          {t('g.pay_now', 'Pay Now')}
        </button>
      </div>
    );
  }

  if (isInGracePeriod) {
    // Dismiss is only allowed for info severity (early grace period — Days 0–3).
    // Warning and critical banners are sticky: they cannot be dismissed.
    if (dismissed && severity === GRACE_PERIOD_SEVERITY.INFO) return null;

    const endDateStr = gracePeriodEndDate ? formatDate(gracePeriodEndDate, 'DD.MM.YYYY') : null;
    const daysLeft = daysRemaining !== undefined ? daysRemaining : null;

    // Severity-based visual theme
    const isEscalated =
      severity === GRACE_PERIOD_SEVERITY.WARNING || severity === GRACE_PERIOD_SEVERITY.CRITICAL;
    const bgColor = isEscalated ? '#fffbeb' : '#e8eefa';
    const borderColor = isEscalated ? '#f59e0b' : '#314d89';
    const iconBgColor = isEscalated ? '#e17100' : '#314d89';
    const textColor = isEscalated ? '#78350f' : '#314c84';
    const subTextColor = isEscalated ? '#92400e' : '#314c84';
    const btnBgColor = isEscalated ? '#e17100' : '#314d89';
    const btnHoverColor = isEscalated ? '#c05e00' : '#243a6b';

    return (
      <div
        className="flex items-center justify-between p-4 rounded-lg mb-2.5 shadow-sm border-b-2 flex-col lg:flex-row gap-4"
        style={{ backgroundColor: bgColor, borderColor }}
      >
        <div className="flex items-center gap-4 flex-1">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: iconBgColor }}
          >
            <svg width="22" height="20" viewBox="0 0 22.016 20.014" fill="none">
              <path
                d="M20.748 16.014L12.748 2.014C12.574 1.706 12.321 1.45 12.015 1.272C11.709 1.094 11.362 1 11.008 1C10.654 1 10.307 1.094 10.001 1.272C9.695 1.45 9.442 1.706 9.268 2.014L1.268 16.014C1.092 16.319 0.999 16.666 1 17.018C1.001 17.371 1.095 17.717 1.272 18.022C1.45 18.326 1.705 18.579 2.012 18.753C2.318 18.927 2.665 19.017 3.018 19.014H19.018C19.369 19.013 19.714 18.921 20.017 18.745C20.321 18.57 20.573 18.317 20.749 18.013C20.924 17.709 21.016 17.364 21.016 17.013C21.016 16.663 20.923 16.318 20.748 16.014Z"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11 8V12"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M11 16H11.01"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <div className="flex items-start gap-1 flex-wrap">
              <span className="text-base font-bold leading-6" style={{ color: textColor }}>
                {t('grace.payment_overdue', 'Payment Overdue:')}
              </span>
              <span className="text-base font-normal leading-6 flex-1" style={{ color: textColor }}>
                {t('grace.unpaid_count', 'You have {{count}} unpaid invoice(s).', {
                  count: unpaidInvoiceCount,
                })}
                {endDateStr && (
                  <>
                    {' '}
                    {t('grace.grace_ends_on', 'Grace period ends on:')}{' '}
                    <strong>{endDateStr}</strong>
                    {daysLeft !== null && (
                      <> {t('grace.days_left', '({{count}} day(s) left)', { count: daysLeft })}</>
                    )}
                    .
                  </>
                )}
              </span>
            </div>
            <div
              className="text-sm font-normal leading-5 opacity-90"
              style={{ color: subTextColor }}
            >
              {t('grace.amount_due', 'Amount due:')} {formatCurrency(outstandingAmount)}
              {unpaidInvoiceCount > 1
                ? ' ' +
                  t('grace.across_invoices', 'across {{count}} invoices', {
                    count: unpaidInvoiceCount,
                  })
                : ''}
              .
            </div>
            <div
              className="text-sm font-normal leading-5 opacity-90"
              style={{ color: subTextColor }}
            >
              {isEscalated
                ? t(
                    'grace.immediate_payment',
                    'Immediate payment required to avoid service interruption.',
                  )
                : t(
                    'billing.banner.grace_hint',
                    'All features remain accessible during this period. Please settle outstanding invoices to avoid service interruption.',
                  )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 w-full lg:w-auto">
          <button
            className="flex items-center justify-center gap-3 py-2.5 px-5 border-none rounded-lg text-white text-base font-normal leading-6 cursor-pointer transition-colors duration-200 h-11 flex-1 lg:flex-none"
            style={{ backgroundColor: btnBgColor }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = btnHoverColor;
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = btnBgColor;
            }}
            onClick={handlePayNow}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2.5 4.5H13.5V13.5H2.5V4.5Z" stroke="white" strokeWidth="1.5" fill="none" />
              <path d="M2.5 7.5H13.5" stroke="white" strokeWidth="1.5" />
              <path d="M5 10.5H7.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {t('grace.pay_now', 'Pay Now')}
          </button>
          {!isEscalated && (
            <button
              className="flex items-center justify-center w-9 h-9 rounded-lg border-none cursor-pointer transition-colors duration-200 flex-shrink-0"
              style={{ backgroundColor: 'transparent' }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  'rgba(49,77,137,0.1)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
              }}
              onClick={() => setDismissed(true)}
              aria-label={t('grace.dismiss', 'Dismiss')}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path
                  d="M1 1L13 13M13 1L1 13"
                  stroke="#314d89"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
        </div>
      </div>
    );
  }

  // Pending invoice state: invoice generated but due date is in the future.
  // isInGracePeriod=false, isRestricted=false but unpaidInvoiceCount > 0.
  // CVA-2714 AC2: blue dismissible banner — "Your invoice is ready."
  if (!isInGracePeriod && !isRestricted && unpaidInvoiceCount > 0) {
    if (dismissed) return null;

    const endDateStr = gracePeriodEndDate ? formatDate(gracePeriodEndDate, 'DD.MM.YYYY') : null;
    const daysLeft = daysRemaining !== undefined ? daysRemaining : null;

    return (
      <div
        className="flex items-center justify-between p-4 rounded-lg mb-2.5 shadow-sm border-b-2 flex-col lg:flex-row gap-4"
        style={{ backgroundColor: '#e8eefa', borderColor: '#314d89' }}
      >
        <div className="flex items-center gap-4 flex-1">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: '#314d89' }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="2" />
              <path
                d="M12 7v5l3 3"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div className="flex flex-col gap-1 flex-1">
            <div className="flex items-start gap-1 flex-wrap">
              <span className="text-base font-normal leading-6 flex-1" style={{ color: '#314c84' }}>
                {t(
                  'grace.invoice_ready',
                  'Your invoice is ready. Please make a payment to ensure uninterrupted access.',
                )}
                {endDateStr && (
                  <>
                    {' '}
                    {t('grace.due_by', 'Due by')} <strong>{endDateStr}</strong>
                    {daysLeft !== null && (
                      <>
                        {' '}
                        {t('grace.days_remaining', '({{count}} day(s) remaining)', {
                          count: daysLeft,
                        })}
                      </>
                    )}
                    .
                  </>
                )}
              </span>
            </div>
            <div className="text-sm font-normal leading-5 opacity-90" style={{ color: '#314c84' }}>
              {t('grace.amount_due', 'Amount due:')} {formatCurrency(outstandingAmount)}
              {unpaidInvoiceCount > 1
                ? ' ' +
                  t('grace.across_invoices', 'across {{count}} invoices', {
                    count: unpaidInvoiceCount,
                  })
                : ''}
              .
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0 w-full lg:w-auto">
          <button
            className="flex items-center justify-center gap-3 py-2.5 px-5 border-none rounded-lg text-white text-base font-normal leading-6 cursor-pointer transition-colors duration-200 h-11 flex-1 lg:flex-none"
            style={{ backgroundColor: '#314d89' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#243a6b';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#314d89';
            }}
            onClick={handlePayNow}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2.5 4.5H13.5V13.5H2.5V4.5Z" stroke="white" strokeWidth="1.5" fill="none" />
              <path d="M2.5 7.5H13.5" stroke="white" strokeWidth="1.5" />
              <path d="M5 10.5H7.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            {t('grace.pay_now', 'Pay Now')}
          </button>
          <button
            className="flex items-center justify-center w-9 h-9 rounded-lg border-none cursor-pointer transition-colors duration-200 flex-shrink-0"
            style={{ backgroundColor: 'transparent' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'rgba(49,77,137,0.1)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent';
            }}
            onClick={() => setDismissed(true)}
            aria-label={t('grace.dismiss', 'Dismiss')}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M1 1L13 13M13 1L1 13"
                stroke="#314d89"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }
  return null;
};

export default GracePeriodBanner;
